import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Download, Refresh } from "@mui/icons-material";

import Page from "./Page";
import useApi from "../../hooks/useApi";
import { exportToExcel } from "../../utils/exportToExcel";

const rowsPerPage = 10;

const SyncReport = () => {
  const navigate = useNavigate();
  const { postData } = useApi();

  const [filters, setFilters] = useState({
    province: "",
    supervisorId: "",
    repId: "",
    dateFrom: "",
    dateTo: "",
    status: "all",
  });

  const [detailRows, setDetailRows] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    provinces: [],
    supervisors: [],
    reps: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0); // 0 = By Rep, 1 = Detail
  const [detailPage, setDetailPage] = useState(0);
  const [summaryPage, setSummaryPage] = useState(0);

  const hasDetailFilters = useMemo(() => {
    const hasScopeFilter = Boolean(
      String(filters?.province || "").trim() ||
      String(filters?.supervisorId || "").trim() ||
      String(filters?.repId || "").trim(),
    );
    const hasDateFilter = Boolean(
      String(filters?.dateFrom || "").trim() ||
      String(filters?.dateTo || "").trim(),
    );
    const hasStatusFilter =
      String(filters?.status || "all")
        .trim()
        .toLowerCase() !== "all";

    return hasScopeFilter || hasDateFilter || hasStatusFilter;
  }, [filters]);

  useEffect(() => {
    let isMounted = true;

    const loadSyncReport = async () => {
      try {
        setLoading(true);

        const detailRequest = hasDetailFilters
          ? postData({
              entityName: "SyncDashboard",
              action: "getSyncReportDetail",
              data: { filters },
            })
          : Promise.resolve({ isSuccessful: true, data: [] });

        const [detailResponse, summaryResponse, optionsResponse] =
          await Promise.all([
            detailRequest,
            postData({
              entityName: "SyncDashboard",
              action: "getSyncReportSummaryByRep",
              data: { filters },
            }),
            postData({
              entityName: "SyncDashboard",
              action: "getSyncReportFilterOptions",
              data: { filters },
            }),
          ]);

        if (!isMounted) return;

        if (optionsResponse?.isSuccessful) {
          setFilterOptions({
            provinces: Array.isArray(optionsResponse?.data?.provinces)
              ? optionsResponse.data.provinces
              : [],
            supervisors: Array.isArray(optionsResponse?.data?.supervisors)
              ? optionsResponse.data.supervisors
              : [],
            reps: Array.isArray(optionsResponse?.data?.reps)
              ? optionsResponse.data.reps
              : [],
          });
        } else {
          setFilterOptions({ provinces: [], supervisors: [], reps: [] });
        }

        if (detailResponse?.isSuccessful) {
          setDetailRows(
            Array.isArray(detailResponse.data) ? detailResponse.data : [],
          );
        } else {
          setDetailRows([]);
        }

        if (summaryResponse?.isSuccessful) {
          setSummaryRows(
            Array.isArray(summaryResponse.data) ? summaryResponse.data : [],
          );
        } else {
          setSummaryRows([]);
        }

        if (detailResponse?.isSuccessful && summaryResponse?.isSuccessful) {
          setError("");
        } else {
          setError("Unable to load sync report data");
        }
      } catch (err) {
        if (!isMounted) return;
        setError(err?.message || "Unable to load sync report data");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadSyncReport();

    return () => {
      isMounted = false;
    };
  }, [filters]);

  const availableFilterOptions = useMemo(() => {
    return {
      provinces: Array.isArray(filterOptions?.provinces)
        ? filterOptions.provinces
        : [],
      supervisors: Array.isArray(filterOptions?.supervisors)
        ? filterOptions.supervisors
        : [],
      reps: Array.isArray(filterOptions?.reps) ? filterOptions.reps : [],
    };
  }, [filterOptions]);

  const handleToolbarClick = (item) => {
    switch (item) {
      case "syncDashboard":
        navigate("/syncdashboard");
        break;
      default:
        break;
    }
  };

  const handleFilterChange = (key) => (event) => {
    const value = event.target.value;
    setFilters((previous) => ({ ...previous, [key]: value }));
    setDetailPage(0);
    setSummaryPage(0);
  };

  const handleRefresh = () => {
    setFilters((previous) => ({ ...previous }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "error":
        return "error";
      default:
        return "default";
    }
  };

  const formatDeviceType = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "-";

    const normalized = raw.toLowerCase();
    if (normalized.includes("android")) return "Android";
    if (normalized.includes("ios") || normalized.includes("iphone")) {
      return "iOS";
    }

    return raw;
  };

  const buildSyncMetaLine = (row, prefix = "") => {
    const fieldPrefix = String(prefix || "");
    const prefixedKey = (baseField) => {
      if (!fieldPrefix) return baseField;
      return `${fieldPrefix}${baseField.charAt(0).toUpperCase()}${baseField.slice(1)}`;
    };

    const deviceType = row?.[prefixedKey("deviceType")] ?? row?.deviceType;
    const osVersion = row?.[prefixedKey("osVersion")] ?? row?.osVersion;
    const appVersion = row?.[prefixedKey("appVersion")] ?? row?.appVersion;

    const device = formatDeviceType(deviceType);
    const safeOsVersion = String(osVersion || "").trim() || "-";
    const safeAppVersion = String(appVersion || "").trim() || "-";

    return {
      deviceInfo: `${device} | OS ${safeOsVersion} | App ${safeAppVersion}`,
      appVersion: safeAppVersion,
    };
  };

  const formatDateTime = (value, dateFormat = "yyyy-MM-dd HH:mm") => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return format(date, dateFormat);
  };

  const formatDuration = (durationMs) => {
    const value = Number(durationMs);
    if (!Number.isFinite(value) || value <= 0) return "-";

    const totalSeconds = Math.floor(value / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }

    return `${seconds}s`;
  };

  const exportCurrentTab = () => {
    const timestamp = format(new Date(), "yyyyMMdd-HHmmss");

    if (activeTab === 0) {
      const exportRows = summaryRows.map((row) => ({
        ...buildSyncMetaLine(row, "last"),
        Rep: row.repName || "Unknown",
        Supervisor: row.supervisorName || "Unassigned",
        Province: row.province || "-",
        "Sync Records": Number(row.totalSyncRecords || 0),
        "Completed Records": Number(row.completedSyncRecords || 0),
        "Error Records": Number(row.errorSyncRecords || 0),
        "Last Successful Sync": formatDateTime(row.lastSuccessfulSyncAt),
        "Last Successful Sync Count": Number(
          row.lastSuccessfulSyncSuccessCount || 0,
        ),
        "Success Count": Number(row.totalSuccessCount || 0),
        "Error Count": Number(row.totalErrorCount || 0),
        "Avg Duration": formatDuration(row.avgDurationMs),
        "First Sync": formatDateTime(row.firstSyncAt),
        "Last Sync": formatDateTime(row.lastSyncAt),
      }));

      exportToExcel(exportRows, `sync-report-summary-${timestamp}`);
      return;
    }

    if (!hasDetailFilters) {
      return;
    }

    const exportRows = detailRows.map((row) => ({
      ...buildSyncMetaLine(row),
      Rep: row.repName || "Unknown",
      Supervisor: row.supervisorName || "Unassigned",
      Province: row.province || "-",
      Status: row.status || "-",
      "Started At": formatDateTime(row.startedAt, "yyyy-MM-dd HH:mm:ss"),
      "Ended At": formatDateTime(row.endedAt, "yyyy-MM-dd HH:mm:ss"),
      Duration: formatDuration(row.durationMs),
      "Success Count": Number(row.successCount || 0),
      "Error Count": Number(row.errorCount || 0),
    }));

    exportToExcel(exportRows, `sync-report-detail-${timestamp}`);
  };

  return (
    <Page
      pageName="syncReport"
      includeToolbar
      onToolItemClick={handleToolbarClick}
      items={[{ name: "syncDashboard", iconName: "Dashboard" }]}
    >
      <Stack spacing={2}>
        <Card elevation={2}>
          <CardContent>
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                md={2}
                sx={{ minWidth: "150px" }}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>Province</InputLabel>
                  <Select
                    value={filters.province}
                    label="Province"
                    onChange={handleFilterChange("province")}
                  >
                    <MenuItem value="">All</MenuItem>
                    {availableFilterOptions.provinces.map((province) => (
                      <MenuItem
                        key={province}
                        value={province}
                      >
                        {province}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                md={3}
                sx={{ minWidth: "150px" }}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>Supervisor</InputLabel>
                  <Select
                    value={filters.supervisorId}
                    label="Supervisor"
                    onChange={handleFilterChange("supervisorId")}
                  >
                    <MenuItem value="">All</MenuItem>
                    {availableFilterOptions.supervisors.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                md={3}
                sx={{ minWidth: "150px" }}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>Rep</InputLabel>
                  <Select
                    value={filters.repId}
                    label="Rep"
                    onChange={handleFilterChange("repId")}
                  >
                    <MenuItem value="">All</MenuItem>
                    {availableFilterOptions.reps.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                md={2}
              >
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Date From"
                  InputLabelProps={{ shrink: true }}
                  value={filters.dateFrom}
                  onChange={handleFilterChange("dateFrom")}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={2}
              >
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Date To"
                  InputLabelProps={{ shrink: true }}
                  value={filters.dateTo}
                  onChange={handleFilterChange("dateTo")}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={2}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={handleFilterChange("status")}
                  >
                    <MenuItem value="all">All Final</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                md={10}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flex-end"
                >
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Download />}
                    onClick={exportCurrentTab}
                    disabled={
                      activeTab === 0
                        ? summaryRows.length === 0
                        : !hasDetailFilters || detailRows.length === 0
                    }
                  >
                    Export Current View
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper>
          <Tabs
            value={activeTab}
            onChange={(_event, tabValue) => setActiveTab(tabValue)}
          >
            <Tab label={`By Rep (${summaryRows.length})`} />
            <Tab label={`Detail (${detailRows.length})`} />
          </Tabs>

          {loading ? (
            <Box p={3}>
              <Typography>Loading sync report...</Typography>
            </Box>
          ) : null}

          {!loading && activeTab === 0 ? (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rep</TableCell>
                      <TableCell>Supervisor</TableCell>
                      <TableCell>Province</TableCell>
                      <TableCell>Last Device Info</TableCell>
                      <TableCell>Last App Version</TableCell>
                      <TableCell align="right">Sync Records</TableCell>
                      <TableCell align="right">Completed</TableCell>
                      <TableCell align="right">Errors</TableCell>
                      <TableCell>Last Successful Sync</TableCell>
                      <TableCell align="right">Last Successful Count</TableCell>
                      <TableCell align="right">Success Count</TableCell>
                      <TableCell align="right">Error Count</TableCell>
                      <TableCell align="right">Avg Duration</TableCell>
                      <TableCell>Last Sync</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summaryRows
                      .slice(
                        summaryPage * rowsPerPage,
                        summaryPage * rowsPerPage + rowsPerPage,
                      )
                      .map((row, index) => (
                        <TableRow
                          key={`${String(row.repId || "unknown")}-${index}`}
                        >
                          <TableCell>{row.repName || "Unknown"}</TableCell>
                          <TableCell>
                            {row.supervisorName || "Unassigned"}
                          </TableCell>
                          <TableCell>{row.province || "-"}</TableCell>
                          <TableCell>
                            {buildSyncMetaLine(row, "last").deviceInfo}
                          </TableCell>
                          <TableCell>
                            {buildSyncMetaLine(row, "last").appVersion}
                          </TableCell>
                          <TableCell align="right">
                            {Number(row.totalSyncRecords || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {Number(row.completedSyncRecords || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {Number(row.errorSyncRecords || 0)}
                          </TableCell>
                          <TableCell>
                            {formatDateTime(row.lastSuccessfulSyncAt)}
                          </TableCell>
                          <TableCell align="right">
                            {Number(row.lastSuccessfulSyncSuccessCount || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {Number(row.totalSuccessCount || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {Number(row.totalErrorCount || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {formatDuration(row.avgDurationMs)}
                          </TableCell>
                          <TableCell>
                            {formatDateTime(row.lastSyncAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={summaryRows.length}
                page={summaryPage}
                onPageChange={(_event, nextPage) => setSummaryPage(nextPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
              />
            </>
          ) : null}

          {!loading && activeTab === 1 ? (
            <>
              {!hasDetailFilters ? (
                <Box p={2}>
                  <Alert severity="info">
                    Add at least one filter (rep, supervisor, province, date, or
                    status) to load detail records.
                  </Alert>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Rep</TableCell>
                          <TableCell>Supervisor</TableCell>
                          <TableCell>Province</TableCell>
                          <TableCell>Device Info</TableCell>
                          <TableCell>App Version</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Started</TableCell>
                          <TableCell>Ended</TableCell>
                          <TableCell align="right">Duration</TableCell>
                          <TableCell align="right">Success</TableCell>
                          <TableCell align="right">Errors</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detailRows
                          .slice(
                            detailPage * rowsPerPage,
                            detailPage * rowsPerPage + rowsPerPage,
                          )
                          .map((row) => (
                            <TableRow key={String(row._id)}>
                              <TableCell>{row.repName || "Unknown"}</TableCell>
                              <TableCell>
                                {row.supervisorName || "Unassigned"}
                              </TableCell>
                              <TableCell>{row.province || "-"}</TableCell>
                              <TableCell>
                                {buildSyncMetaLine(row).deviceInfo}
                              </TableCell>
                              <TableCell>
                                {buildSyncMetaLine(row).appVersion}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={row.status || "-"}
                                  color={getStatusColor(row.status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {formatDateTime(
                                  row.startedAt,
                                  "yyyy-MM-dd HH:mm:ss",
                                )}
                              </TableCell>
                              <TableCell>
                                {formatDateTime(
                                  row.endedAt,
                                  "yyyy-MM-dd HH:mm:ss",
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {formatDuration(row.durationMs)}
                              </TableCell>
                              <TableCell align="right">
                                {Number(row.successCount || 0)}
                              </TableCell>
                              <TableCell align="right">
                                {Number(row.errorCount || 0)}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={detailRows.length}
                    page={detailPage}
                    onPageChange={(_event, nextPage) => setDetailPage(nextPage)}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[rowsPerPage]}
                  />
                </>
              )}
            </>
          ) : null}
        </Paper>
      </Stack>
    </Page>
  );
};

export default SyncReport;
